import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { formatPrice, formatPlanDescription, getFirstTicketPrice, getAttendeeRowPrice, calculateTrainingSubtotal } from '../helpers/CommonHelpers';
import { config } from "../utils/config";
import { validationRules } from "../utils/validationRules";
import { useFormValidator } from "../hooks/useFormValidator";
import { PaymentDateFormat } from "../helpers/CommonHelpers.js";
import { cartToItems, getCurrency, pushEcommEvent } from "../utils/ga4";
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements, AddressElement } from '@stripe/react-stripe-js';
import { useNavigate } from "react-router-dom";

// Get Stripe publishable key from backend configuration
// This ensures the frontend always uses the same key as the backend (test or live)
const getStripePublishableKey = () => {
    const im_ecom = window.im_ecom || {};
    return im_ecom.stripe_publish_key;
};

// Create stripePromise dynamically based on backend configuration
const createStripePromise = () => {
    return loadStripe(getStripePublishableKey());
};


const PaymentForm = ({ billingDetails, amount, im_custom, sessionId, taxAmount = 0, isSetupIntent = false, onTaxUpdate, onPaymentMethodInfoUpdate }) => {
    const navigate = useNavigate();
    const stripe = useStripe();
    const elements = useElements();
    const [message, setMessage] = useState("");
    const [currentTaxAmount, setCurrentTaxAmount] = useState(taxAmount);
    const [isRecalculating, setIsRecalculating] = useState(false);
    const [isPaymentError, setIsPaymentError] = useState(false);
    const addPaymentInfoSentRef = useRef(false);

    // Helper function to check payment status from database
    const checkPaymentStatus = () => {
        if (!sessionId) {
            return Promise.reject(new Error('No session_id available'));
        }
        return fetch(`/wp-json/dom/v1/payment/status?session_id=${encodeURIComponent(sessionId)}`)
            .then(res => {
                if (!res.ok) {
                    throw new Error('Payment status check failed');
                }
                return res.json();
            });
    };

    // Helper function to poll database until payment is confirmed
    const waitForPaymentConfirmation = () => {
        if (!sessionId) {
            // No session_id - navigate immediately (for non-cart payments)
            setMessage("Payment successful!");
            window.scrollTo({ top: 0, behavior: "instant" });
            navigate("/user/thankyou");
            return;
        }

        setMessage("Processing Payment...");

        let pollCount = 0;
        const maxPolls = 150; // Max 5 minutes (150 * 2 seconds)
        const pollDelay = 2000; // 2 seconds

        // Initial check
        checkPaymentStatus()
            .then(data => {
                if (data.is_complete) {
                    // Payment already confirmed
                    setMessage("Payment successful!");
                    window.scrollTo({ top: 0, behavior: "instant" });
                    navigate("/user/thankyou");
                } else if (data.status === 'initiated') {
                    // Payment still processing - start polling
                    const pollInterval = setInterval(() => {
                        pollCount++;

                        // Stop polling after max attempts
                        if (pollCount >= maxPolls) {
                            clearInterval(pollInterval);
                            setMessage("Payment is taking longer than expected. Please check your email for confirmation.");
                            console.warn('Payment polling timeout - max attempts reached');
                            return;
                        }

                        checkPaymentStatus()
                            .then(pollData => {
                                if (pollData.is_complete) {
                                    // Payment completed - stop polling and navigate
                                    clearInterval(pollInterval);
                                    setMessage("Payment successful!");
                                    window.scrollTo({ top: 0, behavior: "instant" });
                                    navigate("/user/thankyou");
                                }
                                // If still initiated, continue polling
                            })
                            .catch(error => {
                                console.error('Error polling payment status:', error);
                                // Continue polling even on error
                            });
                    }, pollDelay);
                } else {
                    // Unknown status - navigate anyway
                    setMessage("Payment successful!");
                    window.scrollTo({ top: 0, behavior: "instant" });
                    navigate("/user/thankyou");
                }
            })
            .catch(error => {
                console.error('Failed to check payment status:', error);
                // Navigate anyway on error
                setMessage("Payment successful!");
                window.scrollTo({ top: 0, behavior: "instant" });
                navigate("/user/thankyou");
            });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsPaymentError(false);
        setMessage("Processing payment...");

        if (!stripe || !elements) return;

        try {
            // GA4 add_payment_info (once per checkout submit)
            if (!addPaymentInfoSentRef.current) {
                let cartData = null;
                try { cartData = JSON.parse(localStorage.getItem("user_cart") || "null"); } catch (err) { cartData = null; }
                const items = cartToItems(cartData || {}, im_custom);
                const currency = getCurrency(im_custom);
                const value = (Number(amount || 0) + Number(currentTaxAmount || 0)) || 0;
                if (items.length) {
                    pushEcommEvent('add_payment_info', {
                        currency,
                        value,
                        items,
                        payment_type: isSetupIntent ? 'setup_intent' : 'payment_intent',
                    });
                    addPaymentInfoSentRef.current = true;
                }
            }

            if (isSetupIntent) {
                // For subscriptions and single purchases: confirm SetupIntent to collect payment method
                const { error, setupIntent } = await stripe.confirmSetup({
                    elements,
                    confirmParams: { return_url: window.location.href },
                    redirect: "if_required",
                });

                if (error) {
                    setIsPaymentError(true);
                    setMessage(`Payment error: ${error.message}`);
                    return;
                }

                if (setupIntent && setupIntent.status === 'succeeded') {
                    const paymentMethodId = setupIntent.payment_method;
                    if (!paymentMethodId) {
                        setMessage("Error: Payment method not found");
                        return;
                    }

                    // Determine if this is a subscription or single purchase based on cart data
                    // Check if cart has activePlan (subscription) or only activeEvent (single purchase)
                    const savedCart = localStorage.getItem("user_cart");
                    const cartData = savedCart ? JSON.parse(savedCart) : null;
                    const isSubscription = cartData && cartData.activePlan && cartData.activePlan.plan_id;

                    if (isSubscription) {
                        // Create subscription with payment method
                        setMessage("Creating subscription...");

                        const response = await fetch('/wp-json/dom/v1/checkout/subscription', {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                                session_id: sessionId,
                                payment_method_id: paymentMethodId,
                                stripe_customer_id: im_custom.user.stripe_customer_id,
                            }),
                        });

                        const data = await response.json();

                        if (!response.ok) {
                            setMessage(`Error: ${data.message || 'Failed to create subscription'}`);
                            return;
                        }

                        // Subscription created - wait for webhook to update database
                        if (data.subscriptionId) {
                            waitForPaymentConfirmation();
                        } else {
                            setMessage("Subscription created but missing subscription ID");
                        }
                    } else {
                        // Create invoice with payment method (for single purchases)
                        setMessage("Creating invoice...");

                        const response = await fetch('/wp-json/dom/v1/checkout/payment-invoice', {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                                session_id: sessionId,
                                payment_method_id: paymentMethodId,
                                stripe_customer_id: im_custom.user.stripe_customer_id,
                            }),
                        });

                        const data = await response.json();

                        if (!response.ok) {
                            setMessage(`Error: ${data.message || 'Failed to create invoice'}`);
                            return;
                        }

                        // Invoice created - wait for webhook to update database
                        if (data.invoiceId || data.paymentIntentId) {
                            waitForPaymentConfirmation();
                        } else {
                            setMessage("Invoice created but missing invoice ID");
                        }
                    }
                }
            } else {
                // For one-time payments: confirm PaymentIntent
                const { error, paymentIntent } = await stripe.confirmPayment({
                    elements,
                    confirmParams: { return_url: window.location.href },
                    redirect: "if_required",
                });

                if (error) {
                    setIsPaymentError(true);
                    setMessage(`Payment error: ${error.message}`);
                    return;
                }

                if (paymentIntent) {
                    // Payment confirmed by Stripe - wait for webhook to update database
                    waitForPaymentConfirmation();
                }
            }
        } catch (err) {
            setMessage(`Error: ${err.message}`);
            console.error('Payment processing error:', err);
        }
    };

    // Store the last address to avoid duplicate calls
    const lastAddressRef = React.useRef(null);
    const debounceTimerRef = React.useRef(null);

    // Handle address change to recalculate tax
    const handleAddressChange = useCallback(async (event) => {
        // Recalculate tax for both subscriptions and one-time purchases

        // Clear any existing debounce timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Wait for address to be complete and valid
        if (!event.complete) {
            return;
        }

        // Get address from the event
        const address = event.value?.address;
        if (!address || !address.line1 || !address.city || !address.state || !address.postal_code || !address.country) {
            return; // Address is not fully populated yet
        }

        // Create a unique key for this address to detect actual changes
        const addressKey = `${address.line1}|${address.city}|${address.state}|${address.postal_code}|${address.country}`;

        // Skip if this is the same address we just processed
        if (lastAddressRef.current === addressKey) {
            return;
        }

        // Debounce the API call to wait for autocomplete selection to settle
        debounceTimerRef.current = setTimeout(async () => {
            // Double-check the address is still complete after debounce
            if (!event.complete) {
                return;
            }

            setIsRecalculating(true);

            try {
                // Call backend to recalculate tax
                const response = await fetch('/wp-json/dom/v1/checkout/recalculate-tax', {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        session_id: sessionId,
                        stripe_customer_id: im_custom.user.stripe_customer_id,
                        address: {
                            line1: address.line1,
                            line2: address.line2 || '',
                            city: address.city,
                            state: address.state,
                            postal_code: address.postal_code,
                            country: address.country,
                        },
                    }),
                });

                const data = await response.json();

                if (response.ok && typeof data.taxAmount === 'number') {
                    // Update the last address key to prevent duplicate calls
                    lastAddressRef.current = addressKey;
                    setCurrentTaxAmount(data.taxAmount);
                    if (onTaxUpdate) {
                        onTaxUpdate(data.taxAmount);
                    }
                    if (onPaymentMethodInfoUpdate) {
                        onPaymentMethodInfoUpdate({
                            name: event.value?.name || "",
                            email: event.value?.email || "",
                            address: {
                                line1: address.line1,
                                line2: address.line2 || '',
                                city: address.city,
                                state: address.state,
                                postal_code: address.postal_code,
                                country: address.country,
                            },
                        });
                    }
                }
            } catch (err) {
                console.error('Error recalculating tax:', err);
            } finally {
                setIsRecalculating(false);
            }
        }, 500); // Wait 500ms after address is complete before calling API
    }, [sessionId, im_custom.user.stripe_customer_id, onTaxUpdate]);

    // Cleanup debounce timer on unmount
    React.useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // Update current tax amount when prop changes
    useEffect(() => {
        setCurrentTaxAmount(taxAmount);
    }, [taxAmount]);

    return (
        <form onSubmit={handleSubmit}>
            <h3>Billing Details</h3>
            <AddressElement
                options={{
                    mode: "billing",
                    defaultValues: billingDetails,
                }}
                onChange={handleAddressChange}
            />
            <h3 style={{ marginTop: 20 }}>Payment</h3>
            <PaymentElement
                options={{
                    paymentMethodTypes: ['card', 'us_bank_account'],
                    wallets: 'never'
                }}
            />
            {stripe && (
                <p className="legal-text">By placing your order, you agree to the  <a href="/services/payments-cancellations-refunds-terms-amp-conditions/" target="_blank">Terms and Refund Policy</a></p>
            )}

            <button
                disabled={!stripe || isRecalculating}
                className="custom-button w-100 mt-3"
            >
                {isRecalculating ? 'Recalculating...' : `Pay ${formatPrice(amount + currentTaxAmount)}`}
            </button>

            {message && (
                <p style={{
                    marginTop: "10px",
                    textAlign: "center",
                    color: isPaymentError ? "red" : "green"
                }}>
                    {message}
                </p>
            )}
        </form>
    );
};

export function PaymentInformation({ im_custom, userCart, grandTotal, onTaxUpdate, onPaymentMethodInfoUpdate }) {
    const [clientSecret, setClientSecret] = useState(null);
    const [taxAmount, setTaxAmount] = useState(0);
    const [isSetupIntent, setIsSetupIntent] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Loading payment form...');

    // Create stripePromise using the publishable key from backend configuration
    // This ensures it matches the backend's secret key (test or live)
    const stripePromise = useMemo(() => createStripePromise(), []);

    useEffect(() => {
        if (grandTotal > 0) {
            fetch('/wp-json/dom/v1/checkout/cart', {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    session_id: userCart.sessionID,
                    amount: grandTotal * 100,
                    stripe_customer_id: im_custom.user.stripe_customer_id,
                    email: im_custom.user.email,
                    user_id: im_custom.user.user_id,

                    activeEvent: userCart.activeEvent || null,
                    event_id: userCart.activeEvent?.event_id || 0,
                    attendees: userCart.attendees,

                    activePlan: userCart.activePlan || null,
                    plan_id: userCart.activePlan?.plan_id || 0,
                    // atlas_users: atlas_users,
                    atlas_users: userCart.users,
                }),
            })
                .then((res) => res.json())
                .then((data) => {
                    if (data.clientSecret) {
                        setClientSecret(data.clientSecret);
                        // Check if this is a SetupIntent (for subscriptions)
                        setIsSetupIntent(data.type === 'setup_intent');
                    }
                    // Update tax amount if provided
                    if (onTaxUpdate && typeof data.taxAmount === 'number') {
                        onTaxUpdate(data.taxAmount);
                        setTaxAmount(data.taxAmount);
                    }
                    if (data.code && data.code === "stripe_error") {
                        setLoadingMessage(data.message);
                    }
                })
                .catch((err) => console.log("Error: " + err.message));
        }
    }, []);
    const appearance = { theme: "stripe" };
    const billingDetails = {
        name: userCart.paymentMethodInfo?.name || im_custom.user.name,
        address: {
            line1: userCart.paymentMethodInfo?.address?.line1 || "",
            line2: userCart.paymentMethodInfo?.address?.line2 || "",
            city: userCart.paymentMethodInfo?.address?.city || "",
            state: userCart.paymentMethodInfo?.address?.state || "",
            postal_code: userCart.paymentMethodInfo?.address?.postal_code || "",
            country: userCart.paymentMethodInfo?.address?.country || ""
        }
    }
    const options = {
        clientSecret, appearance,
        defaultValues: {
            billingDetails: billingDetails,
        },
    };

    return (
        <div style={{ maxWidth: "500px", margin: "50px auto" }}>
            {clientSecret ? (
                <Elements stripe={stripePromise} options={options}>
                    <PaymentForm billingDetails={billingDetails} amount={grandTotal} im_custom={im_custom} sessionId={userCart.sessionID} taxAmount={taxAmount} isSetupIntent={isSetupIntent} onTaxUpdate={onTaxUpdate} onPaymentMethodInfoUpdate={onPaymentMethodInfoUpdate} />
                </Elements>
            ) : (
                <p>{loadingMessage}</p>
            )}
        </div>
    );
}

export function PayerInformation({ im_custom }) {
    return (<div className="payment-information-card">
        <h3 className="payment-information-heading">Payer Information</h3>
        <div className="row"> <div className="col-sm-5 col-lg-4">
            <p className="public-desc">{im_custom.user.name}</p>
            <p className="public-desc">{im_custom.user.email}</p>
            <p className="public-desc">{im_custom.user.phone}</p>
        </div>
            <div className="col-sm-5 col-lg-4">
                <p className="public-desc"> {im_custom.user.company} <br /> {im_custom.user.country} </p>
            </div>
        </div>
    </div>);
}

//  Reusable Grand Total Function
export const calculateGrandTotal = (cart, im_custom) => {

    let total = 0;
    let planSubtotal = 0;
    let eventSubtotal = 0;
    let extraUsersPriceLocal = 0;

    if (!cart) return total;

    // Calculate extra users price - match OrderSummary logic exactly
    if (cart.users?.length > 0) {
        extraUsersPriceLocal = cart.users
            .filter((u) => u.isDefault === "0")
            .reduce((sum, u) => sum + Number(u.price || 0), 0);
    }

    if (cart.activePlan && cart.activePlan.plan_id) {
        // Plan subtotal - match OrderSummary line 521-523 exactly
        planSubtotal = Number(cart.activePlan.price || 0) + extraUsersPriceLocal;
        eventSubtotal = calculateTrainingSubtotal(cart, im_custom);
    }

    if (!cart.activePlan && cart.attendees.length > 0) {
        eventSubtotal = calculateTrainingSubtotal(cart, im_custom);
    }

    total = planSubtotal + eventSubtotal;
    return total;
};

export function OrderSummary({ userCart, im_custom, grandTotal, continueButtonText, continueButtonHandle, estimatedTax = 0 }) {
    return (<><div className="order-summary">
        <h3>Order Summary</h3>
        <ul className="order-list-wrap">

            {userCart.activePlan && (
                <li className="order-list-item active">
                    <div>Atlas Membership</div>
                    <div className="price">{formatPrice(userCart.activePlan.price)}</div>
                </li>
            )}


            {userCart.activePlan && userCart.users.filter((item) => item.isDefault === "0").length > 0 && (
                <li className="order-list-item">
                    <div>
                        Additional Users (
                        {userCart.users.filter((item) => item.isDefault === "0").length})
                    </div>
                    <div className="price">
                        {formatPrice(
                            userCart.users
                                .filter((item) => item.isDefault === "0")
                                .reduce((total, item) => total + item.price, 0)
                        )}
                    </div>
                </li>
            )}

            {userCart.activePlan && (
                <li className="order-list-item">

                    <div> <strong>Subtotal</strong></div>
                    <div className="price">

                        <strong>{formatPrice(userCart.activePlan.price + userCart.users
                            .filter((item) => item.isDefault === "0")
                            .reduce((total, item) => total + item.price, 0))}
                        </strong> <span>12 Month Subscription</span>
                    </div>

                </li>)}

            {/* Microsoft Licensing Training */}
            {userCart.attendees.length > 0 && (
                <li className="order-list-item active mt-4">
                    <div>Microsoft Licensing Training</div>
                    <div className="price">
                        {userCart.activePlan?.training_tickets > 0 ? (
                            <>
                                <span
                                    style={{
                                        textDecoration: "line-through",
                                        marginRight: "8px",
                                        opacity: 0.6,
                                    }}
                                >
                                    {userCart.activePlan.training_tickets == 1 && (formatPrice(im_custom.event_price.price))}
                                    {userCart.activePlan.training_tickets > 1 && (formatPrice(im_custom.event_price.price + (userCart.activePlan.training_tickets - 1) * im_custom.event_price.extra_training_tickets_price))}
                                </span>
                                <span style={{
                                    display: "block",
                                    fontSize: "14px",
                                }}>
                                    {(() => {
                                        const free = userCart.activePlan.training_tickets;
                                        const selected = userCart.activeEvent?.training_tickets || 0;
                                        const extra = Math.max(selected - free, 0);
                                        return formatPrice(extra * userCart.activePlan.extra_training_tickets_price);
                                    })()} (Included in plan)
                                </span>
                            </>
                        ) : (
                            formatPrice(getFirstTicketPrice(userCart, im_custom))
                        )}
                    </div>
                </li>

            )}

            {/* Additional Attendees */}
            {userCart.attendees.filter((i) => i.isDefault === "0").length > 0 && (
                <li className="order-list-item">
                    <div>
                        Additional Attendees (
                        {userCart.attendees.filter((i) => i.isDefault === "0").length})
                    </div>

                    <div className="price">
                        {formatPrice(
                            userCart.attendees.filter((i) => i.isDefault === "0").reduce((t, i) => t + i.price, 0)
                        )}
                    </div>
                </li>
            )}


            {/* Subtotal (merged single block) */}
            {(() => {
                if (userCart.attendees.length == 0) return null;
                const subtotal = calculateTrainingSubtotal(userCart, im_custom);
                return (
                    <li className="order-list-item">
                        <div><strong>Subtotal</strong></div>
                        <div className="price">
                            <strong>{formatPrice(subtotal)}</strong>
                        </div>
                    </li>
                );
            })()}

            {/* Tax */}
            {estimatedTax > 0 && (
                <li className="order-list-item mt-4">
                    <div>Tax</div>
                    <div className="price">{formatPrice(estimatedTax)}</div>
                </li>
            )}
        </ul>

        <div className="check-out-btn">

            <div className="estimated-total">
                <div>Grand Total</div>
                <div className="price">
                    {formatPrice(grandTotal + estimatedTax)}
                </div>
            </div>

            <button
                onClick={continueButtonHandle}
                className="custom-button"

            >
                {continueButtonText}
            </button>
        </div>
    </div></>)
}

export function CartFeaturedPlanItem({ im_custom }) {
    return (
        <>
            <div className="mt-4" style={{ borderTop: "1px solid #D9D9D9" }}></div>
            <div className="research microsoft-training">
                <div class="microsoft-training-one">
                    <div class="row">
                        <div className="col-2 col-sm-10 col-lg-4 col-xl-3 mb-3">
                            <div class="image-wrap">
                                <img src="/wp-content/themes/dom/assets/img/atlas-laptop.webp" alt="Atlas Membership" />
                            </div>
                        </div>
                        <div class="col-lg-8 col-xl-9">
                            <div class="research-one">
                                <div>
                                    <h3>Atlas Membership</h3>
                                    <div class="row training-listing mt-3">
                                        <div class="col-xl-12 col-md-12">
                                            <ul>
                                                {im_custom.plans.map((plan, index) => (
                                                    index <= 2 && (
                                                        <li key={plan.plan_id}>
                                                            <b>{formatPlanDescription(im_custom.event_price, im_custom.plans, plan)} </b><br />
                                                            {plan.atlas_users} Atlas {plan.atlas_users === 1 ? 'Membership' : 'Memberships'}
                                                            {plan.training_tickets > 0 && (
                                                                <>
                                                                    <br />{plan.training_tickets} Microsoft Licensing Training {plan.training_tickets === 1 ? 'Ticket' : 'Tickets'}
                                                                </>
                                                            )}
                                                        </li>
                                                    )
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="w-200">
                        <div>
                            <a href="/become-a-member" class="event-content-button">Membership Details</a>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export function CartFeaturedEventItem({ im_custom, addToCart }) {
    return (
        <><div className="mt-4" style={{ borderTop: "1px solid #D9D9D9" }}></div><div className="research microsoft-training">
            <div className="microsoft-training-one">
                <div className="row">
                    <div className="col-2 col-sm-10 col-lg-3 mb-3">
                        <div className="image-wrap">
                            <img src="/wp-content/themes/dom/assets/img/LBC-ticket-thumbnail.png"
                                alt="Atlas Membership" />
                        </div>
                    </div>
                    <div className="col-lg-9">
                        <div className="research-one">
                            <div>
                                <h3>Microsoft Licensing Training</h3>
                                <p>
                                    Our intensive, 18+ hour training equips you with comprehensive
                                    and up-to-date knowledge and tools to evaluate Microsoft
                                    licensing options confidently and maintain compliance without
                                    overbuying. You'll learn a disciplined process and proven
                                    negotiation strategies to optimize your next Enterprise
                                    Agreement.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="w-200">
                    <div className="price">{`${formatPrice(im_custom.event_price.price)}`}</div>
                    <div>
                        <button
                            className="custom-button ms-md-auto"
                            style={{ minWidth: "fit-content" }}
                            onClick={addToCart}
                        >
                            Add to Cart
                        </button>
                    </div>
                </div>
            </div>
            <div className="row training-listing">
                <div className="col-xl-4 col-md-6">
                    <h3>What You’ll Learn</h3>
                    <ul>
                        <li>Microsoft’s licensing vocabulary, concepts and policies</li>
                        <li>How Microsoft’s key products and cloud services are licensed</li>
                        <li>A framework on which to build your licensing expertise</li>
                        <li>The latest information on shifts and trends in Microsoft licensing</li>
                    </ul>
                </div>
                <div className="col-xl-4 col-md-6">
                    <h3>Who Should Attend</h3>
                    <ul>
                        <li>IT decision-makers</li>
                        <li>Procurement and Legal professionals</li>
                        <li>Software Asset Managers</li>
                        <li>Solution Partners</li>
                        <li>Anyone involved with Licensing Microsoft technologies</li>
                    </ul>
                </div>
            </div>
        </div></>);
}

export function ConfirmPopup({ title, message, onConfirm, onCancel }) {
    return (
        <div
            className="remove-cart modal fade show"
            style={{ display: "block" }}
        >
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header">
                        <h3>{title}</h3>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onCancel}
                        ></button>
                    </div>
                    <div className="modal-body">
                        <p>
                            {message}
                        </p>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn custom-secondary-btn text-decoration-none"
                            onClick={onCancel}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn event-content-button"
                            onClick={onConfirm}
                        >
                            Remove from Cart
                        </button>
                    </div>
                </div>
            </div>
        </div>)
}

export function AllreadyHaveAccountHTML() {
    return (<div className="account-status">
        <div className="account-status-des">
            <h2>Already have an account?</h2>
            <p>
                Speed up the check-out process and log in to your existing
                account or <a href="/user/register">Create an account.</a>
            </p>
        </div>
        <div>
            <a href="/login?redirect_to=/cart" className="event-content-button">
                Login
            </a>
        </div>
    </div>);
}

export function EmptyCartHTML() {
    return (
        <div className="about-wrapper">
            <div className="about-container cart-main text-center">
                <h1 className="m-auto">Your Cart is Empty</h1>
                <p className="m-auto mb-3">
                    Looks like you haven’t added anything yet.
                </p>
                <a href="/become-a-member/" className="custom-button mx-auto mb-3">
                    Continue Shopping
                </a>
            </div>
        </div>
    );
}

export function Tooltip({ text }) {
    return (<div className="leave-tooltip-wrapper ms-2">
        <svg
            width="81"
            height="81"
            viewBox="0 0 81 81"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect
                x="2.835"
                y="2.835"
                width="75.33"
                height="75.33"
                rx="37.665"
                stroke="#888888"
                strokeWidth="5.67"
            />
            <path
                d="M30.8745 32.1275C35.1713 31.6845 38.9144 30.7764 42.1038 29.4032C42.6575 29.6247 43.0451 29.9126 43.2666 30.267C43.2666 41.7621 43.3552 49.9791 43.5324 54.9182C43.5545 55.4941 43.7206 55.9924 44.0307 56.4132C44.3629 56.8119 44.828 57.122 45.4261 57.3435C46.0241 57.565 46.5224 57.7311 46.9211 57.8418C47.3197 57.9304 47.8402 58.019 48.4825 58.1076C48.6154 58.3734 48.6819 58.6502 48.6819 58.9382C48.6819 59.2261 48.6154 59.4697 48.4825 59.6691C45.5589 59.5362 42.7239 59.4697 39.9775 59.4697C37.2754 59.4697 34.4626 59.5362 31.539 59.6691C31.4504 59.3147 31.4061 59.0711 31.4061 58.9382C31.4061 58.8274 31.4504 58.5506 31.539 58.1076C32.2699 58.019 32.8679 57.9304 33.333 57.8418C33.7981 57.7311 34.374 57.565 35.0606 57.3435C35.7472 57.122 36.2677 56.8119 36.6221 56.4132C36.9764 55.9924 37.1647 55.4941 37.1868 54.9182C37.2754 52.7255 37.3197 50.0677 37.3197 46.9448C37.3197 45.9038 37.3087 44.1319 37.2865 41.6292C37.2644 39.1264 37.2533 36.9337 37.2533 35.0511C35.9908 34.2759 33.8646 33.8218 30.8745 33.6889C30.7859 33.3346 30.7416 33.0577 30.7416 32.8584C30.7416 32.7255 30.7859 32.4819 30.8745 32.1275ZM36.0905 18.2404C36.0905 17.1108 36.4892 16.1474 37.2865 15.35C38.106 14.5527 39.0695 14.154 40.1769 14.154C41.3064 14.154 42.2699 14.5527 43.0672 15.35C43.8646 16.1474 44.2633 17.1108 44.2633 18.2404C44.2633 19.3478 43.8646 20.3113 43.0672 21.1308C42.2699 21.9281 41.3064 22.3268 40.1769 22.3268C39.0695 22.3268 38.106 21.9281 37.2865 21.1308C36.4892 20.3113 36.0905 19.3478 36.0905 18.2404Z"
                fill="#888888"
            />
        </svg>
        <span className="tooltip-text">{text}</span>
    </div>);
}

export function EditPersonButton(user, updatePerson) {
    return (<a
        className="btn-info info-btn-t"
        onClick={(e) => {
            e.preventDefault();
            updatePerson(user.id, { openUserForm: "1" });
        }}
    >
        <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <mask
                id={`mask0_${user.id}`}
                maskType="alpha"
                maskUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="20"
                height="20"
            >
                <rect width="20" height="20" fill="#D9D9D9" />
            </mask>
            <g mask={`url(#mask0_${user.id})`}>
                <path
                    d="M4.16602 15.8307H5.21727L13.7477 7.30031L12.6964 6.24906L4.16602 14.7795V15.8307ZM3.66935 17.0807C3.45588 17.0807 3.27699 17.0085 3.13268 16.8641C2.98824 16.7198 2.91602 16.5409 2.91602 16.3274V14.8836C2.91602 14.6805 2.95504 14.4868 3.0331 14.3026C3.11102 14.1184 3.21838 13.958 3.35518 13.8211L13.9081 3.27302C14.0341 3.15858 14.1732 3.07017 14.3254 3.00781C14.4778 2.94531 14.6375 2.91406 14.8046 2.91406C14.9716 2.91406 15.1334 2.94372 15.29 3.00302C15.4466 3.06233 15.5853 3.15663 15.706 3.28594L16.7237 4.31635C16.853 4.43705 16.9452 4.57594 17.0002 4.73302C17.0552 4.8901 17.0827 5.04719 17.0827 5.20427C17.0827 5.37191 17.0541 5.53184 16.9969 5.68406C16.9396 5.83642 16.8486 5.97559 16.7237 6.10156L6.1756 16.6416C6.03879 16.7784 5.87831 16.8857 5.69414 16.9636C5.50997 17.0417 5.31629 17.0807 5.1131 17.0807H3.66935ZM13.2129 6.78385L12.6964 6.24906L13.7477 7.30031L13.2129 6.78385Z"
                    fill="#757575"
                />
            </g>
        </svg>
    </a>);
}

export function DeletePersonButton({ id, removePerson }) {
    return (<button
        type="button"
        onClick={() => removePerson(id)}
        style={{
            background: "transparent",
            border: "none",
            marginLeft: "8px",
            cursor: "pointer",
        }}
        title="Delete User"
    >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
        >
            <mask
                id={`mask-delete-${id}`}
                style={{ maskType: "alpha" }}
                maskUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="20"
                height="20"
            >
                <rect width="20" height="20" fill="#D9D9D9" />
            </mask>
            <g mask={`url(#mask-delete-${id})`}>
                <path
                    d="M6.08975 17.0861C5.67549 17.0861 5.32085 16.9386 5.02585 16.6436C4.73084 16.3486 4.58333 15.9939 4.58333 15.5797V5.00277H4.375C4.19792 5.00277 4.04948 4.94285 3.92969 4.823C3.8099 4.70317 3.75 4.55467 3.75 4.3775C3.75 4.20035 3.8099 4.05194 3.92969 3.93229C4.04948 3.81263 4.19792 3.75279 4.375 3.75279H7.49998C7.49998 3.54874 7.57183 3.37486 7.71552 3.23117C7.85922 3.08747 8.03309 3.01562 8.23715 3.01562H11.7628C11.9669 3.01562 12.1407 3.08747 12.2844 3.23117C12.4281 3.37486 12.5 3.54874 12.5 3.75279H15.625C15.802 3.75279 15.9505 3.81272 16.0703 3.93256C16.1901 4.05241 16.25 4.20091 16.25 4.37806C16.25 4.55523 16.1901 4.70364 16.0703 4.82329C15.9505 4.94294 15.802 5.00277 15.625 5.00277H15.4166V15.5797C15.4166 15.9939 15.2691 16.3486 14.9741 16.6436C14.6791 16.9386 14.3245 17.0861 13.9102 17.0861H6.08975ZM14.1666 5.00277H5.83331V15.5797C5.83331 15.6545 5.85735 15.7159 5.90544 15.764C5.95352 15.8121 6.01496 15.8361 6.08975 15.8361H13.9102C13.985 15.8361 14.0464 15.8121 14.0945 15.764C14.1426 15.7159 14.1666 15.6545 14.1666 15.5797V5.00277ZM8.46181 14.1694C8.63897 14.1694 8.78737 14.1095 8.90702 13.9898C9.02669 13.87 9.08652 13.7215 9.08652 13.5445V7.29442C9.08652 7.11735 9.0266 6.96892 8.90675 6.84913C8.7869 6.72933 8.6384 6.66944 8.46125 6.66944C8.28408 6.66944 8.13567 6.72933 8.01602 6.84913C7.89637 6.96892 7.83654 7.11735 7.83654 7.29442V13.5445C7.83654 13.7215 7.89647 13.87 8.01631 13.9898C8.13615 14.1095 8.28465 14.1694 8.46181 14.1694ZM11.5387 14.1694C11.7159 14.1694 11.8643 14.1095 11.9839 13.9898C12.1036 13.87 12.1634 13.7215 12.1634 13.5445V7.29442C12.1634 7.11735 12.1035 6.96892 11.9836 6.84913C11.8638 6.72933 11.7153 6.66944 11.5381 6.66944C11.361 6.66944 11.2126 6.72933 11.0929 6.84913C10.9733 6.96892 10.9134 7.11735 10.9134 7.29442V13.5445C10.9134 13.7215 10.9734 13.87 11.0932 13.9898C11.2131 14.1095 11.3616 14.1694 11.5387 14.1694Z"
                    fill="#757575"
                />
            </g>
        </svg>
    </button>);
}

export function BackToCartButton({ onClick, label = "Back To Cart" }) {
    return (
        <a className="create-account-back" onClick={onClick}>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
            >
                <path
                    d="M3.4 8.575L8.3 13.475C8.5 13.675 8.59583 13.9083 8.5875 14.175C8.57917 14.4417 8.475 14.675 8.275 14.875C8.075 15.0583 7.84167 15.1542 7.575 15.1625C7.30833 15.1708 7.075 15.075 6.875 14.875L0.275 8.275C0.175 8.175 0.104167 8.06667 0.0625 7.95C0.0208333 7.83333 0 7.70833 0 7.575C0 7.44167 0.0208333 7.31667 0.0625 7.2C0.104167 7.08333 0.175 6.975 0.275 6.875L6.875 0.275C7.05833 0.0916667 7.2875 0 7.5625 0C7.8375 0 8.075 0.0916667 8.275 0.275C8.475 0.475 8.575 0.7125 8.575 0.9875C8.575 1.2625 8.475 1.5 8.275 1.7L3.4 6.575H14.575C14.8583 6.575 15.0958 6.67083 15.2875 6.8625C15.4792 7.05417 15.575 7.29167 15.575 7.575C15.575 7.85833 15.4792 8.09583 15.2875 8.2875C15.0958 8.47917 14.8583 8.575 14.575 8.575H3.4Z"
                    fill="#1C1B1F"
                />
            </svg>
            {label}
        </a>
    );
}

export function PersonInfoForm({ index, user, updatePerson, options }) {
    // Attendees = LBC ticket holders → enterprise domains must NOT be blocked.
    // Users/seats  = membership seats → standard blacklist rules apply..
    const blacklistContext = options?.type === 'attendees' ? 'lbc' : 'membership';
    const { values, errors, loading, fieldLoading, handleBlur, handleChange, handleSubmit, handleClear } =
        useFormValidator(
            user,
            {
                firstName: [validationRules.required],
                lastName: [validationRules.required],
                email: [
                    validationRules.required,
                    validationRules.email,
                    // Wrap in arrow fn so validator is called lazily per keystroke, not immediately.
                    (value) => validationRules.asyncEmailBlacklistCheck(value, blacklistContext),
                ],
                confirmEmail: [
                    validationRules.required,
                    validationRules.confirmEmail(() => values.email),
                ],
            }
        );

    const onSubmit = (id, data) => {
        updatePerson(id, { "firstName": data.firstName, "lastName": data.lastName, "email": data.email, "confirmEmail": data.confirmEmail, "openUserForm": "0" });
    };

    const onClear = (id) => {
        const isAttendee = options.type === "attendees";
        updatePerson(id, {
            "leaveSeatUnassigned": isAttendee ? "0" : "1",  // unchecked attendee
            "firstName": "",
            "lastName": "",
            "email": "",
            "confirmEmail": "",
            "openUserForm": isAttendee ? "1" : "0",          // open attendee form 
        });
    };

    return (<div className="lnfo-form" style={{ display: "block" }}>
        <form onSubmit={handleSubmit(user.id, onSubmit)}>
            <div className="row">
                <div className="col-md-6">
                    <div className="floating-label">
                        <input
                            type="text"
                            id={`org_first_${index}`}
                            name="firstName"
                            value={values.firstName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <label htmlFor={`org_first_${index}`}>First Name*</label>
                        {errors.firstName && (<span className="error">{errors.firstName}</span>)}
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="floating-label">
                        <input
                            type="text"
                            id={`org_last_${index}`}
                            name="lastName"
                            value={values.lastName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <label htmlFor={`org_last_${index}`}>Last Name*</label>
                        {errors.lastName && (<span className="error">{errors.lastName}</span>)}
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-md-6">
                    <div className="floating-label">
                        <input
                            type="email"
                            id={`org_email_${index}`}
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            readOnly={fieldLoading.email}
                        />
                        <label htmlFor={`org_email_${index}`}>Email Address*</label>
                        {fieldLoading.email && <span>⏳ checking...</span>}
                        {errors.email && (<span className="error">{errors.email}</span>)}
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="floating-label">
                        <input
                            type="email"
                            id={`org_email_confirm_${index}`}
                            name="confirmEmail"
                            value={values.confirmEmail}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <label htmlFor={`org_email_confirm_${index}`}>
                            Confirm Email Address*
                        </label>
                        {errors.confirmEmail && (<span className="error">{errors.confirmEmail}</span>)}
                    </div>
                </div>
            </div>
            <div className="lnfo-form-wrap">
                <button
                    disabled={loading || fieldLoading.email}
                    type="button"
                    className="custom-secondary-btn"
                    style={{ marginLeft: "10px" }}
                    onClick={handleClear(user.id, onClear)}>
                    Clear
                </button>
                <button type="submit" className="event-content-button" disabled={loading || fieldLoading.email}>
                    {loading ? "Submitting..." : `Save ${options.type === "attendees" ? "Attendee" : "User"}`}
                </button>
            </div>
        </form>
    </div>);
}


export function PersonList({ people, addPerson, removePerson, updatePerson, options }) {

    return (
        <ul className="lnfo-list-wrap check-lnfo-list">
            {people.map((user, index) => (
                <li key={index} id={`attendee-${user.id}`} className="lnfo-list">
                    <div className="d-flex">
                        <div className="lnfo">
                            {user.firstName && user.lastName && user.openUserForm == "0" ? `${user.firstName} ${user.lastName}` : options.type === "attendees" ? "Attendee Info" : "User Info"}
                        </div>
                     {options.type !== "attendees" && (
                        <label className="lnfo-des">
                            <input type="checkbox" className="filter-checkbox" checked={user.leaveSeatUnassigned == 1 ? true : false}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        updatePerson(user.id, {
                                            leaveSeatUnassigned: "1",
                                            openUserForm: "0",
                                            firstName: "",
                                            lastName: "",
                                            email: "",
                                            confirmEmail: "",
                                            errors: {},
                                        });
                                    } else {
                                        updatePerson(user.id, { leaveSeatUnassigned: "0", openUserForm: "1" });
                                    }
                                }} />Leave seat unassigned
                            {user.leaveSeatUnassigned == "1" && user.openUserForm == "0" && options.type !== "attendees" && (<span 
                                onClick={(e) => {
                                    e.preventDefault();
                                    updatePerson(user.id, { leaveSeatUnassigned: "0", openUserForm: "1" });
                                }}>
                                {user.leaveSeatUnassigned == "1" && user.openUserForm == "0" && (Tooltip({ text: "You can assign the seat at a later time." }))}
                            </span>)}
                        </label>
                    )}
                    </div>
                    <div className="d-flex align-items-center ms-auto">
                        {user.firstName && user.lastName && user.openUserForm == "0" && (
                            <>
                                <span className="lnfo">{(() => {
                                    // Once 2+ tickets are purchased, the first (paid) seat also gets the
                                    // bulk discount (attendees only). Plan-included seats are seeded at
                                    // price 0 and stay "Included in Plan".
                                    const rowPrice = options.type === "attendees"
                                        ? getAttendeeRowPrice(index, user, people.length, options.extraPersonPrice)
                                        : user.price;
                                    return rowPrice > 0
                                        ? `${index > 0 ? "Additional " : ""}${options.type === "attendees" ? "Attendee" : "User"} @ ${formatPrice(rowPrice)}`
                                        : "Included in Plan";
                                })()}</span>
                                {EditPersonButton(user, updatePerson)}
                            </>
                        )}
                    </div>
                    {user.leaveSeatUnassigned == "1" && user.openUserForm == "0" && options.type !== "attendees" && (<a href="#"
                        className="btn-lnfo ms-auto"
                        onClick={(e) => {
                            e.preventDefault();
                            updatePerson(user.id, { leaveSeatUnassigned: "0", openUserForm: "1" });
                        }}>
                        Assign User
                    </a>)}
                    {user.isDefault == 0 && (DeletePersonButton({ id: user.id, removePerson }))}

                    {user.openUserForm == "1" && (<PersonInfoForm index={index} user={user} updatePerson={updatePerson} options={options} />)}
                </li>
            ))}

            <li className="lnfo-list justify-content-center">
                <div
                    className="btn-lnfo"
                    onClick={addPerson}
                    style={{ cursor: "pointer" }}
                >
                    Add Additional {options.type === "attendees" ? "Attendee" : "User"}
                    @ {options.extraPersonPrice ? `${formatPrice(options.extraPersonPrice)}` : ""}
                </div>
            </li>
        </ul >
    );
}

export function InvoiceForm({ im_custom, userCart, onPaymentMethodInfoUpdate }) {
    // Form values
    const [formData, setFormData] = useState({
        session_id:  "",
        email: "",
        name: "",
        company: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipcode: "",
        country: "",
        comments: "",
        purchaseNumber: "",
        contact: "",
        contactEmail: "",
        department: "",
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [errors, setErrors] = useState({});
    const navigate = useNavigate();
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

    // Validation function for visible/used fields
    const validateForm = () => {
        let tempErrors = {};
        const requiredFields = [
            "email",
            "purchaseNumber",
            "contact",
            "contactEmail",
            "department",
            "address1",
            "city",
            "state",
            "zipcode",
            "country",
            // "company", // uncomment if company is required and field is visible
            // "comments", // uncomment if you add comments textarea back
        ];

        if (!formData.email) {
            tempErrors.email = "This field is required";
        } else if (!emailRegex.test(formData.email)) {
            tempErrors.email = "Enter a valid email";
        }

        // Contact Email validation — SAME as email
        if (!formData.contactEmail) {
            tempErrors.contactEmail = "This field is required";
        } else if (!emailRegex.test(formData.contactEmail)) {
            tempErrors.contactEmail = "Enter a valid email";
        }

        // Required fields normal check
        requiredFields.forEach((field) => {
            if (!String(formData[field] ?? "").trim()) {
                if (!tempErrors[field]) tempErrors[field] = "This field is required";
            }
        });

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
        if (onPaymentMethodInfoUpdate) {
            onPaymentMethodInfoUpdate(formData);
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        let msg = "";
        if (!String(value ?? "").trim()) {
            msg = "This field is required";
        }
        if ((name === "email" || name === "contactEmail") && String(value ?? "").trim()) {
            if (!emailRegex.test(value)) {
                msg = "Enter a valid email";
            }
        }
        setErrors((prev) => ({ ...prev, [name]: msg }));
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage("");

        // Do validation
        if (!validateForm()) {
            setLoading(false);
            return;
        }

        const payload = {
            type: "invoice",
            session_id: userCart.sessionID,
            activeEvent: userCart.activeEvent || null,
            event_id: userCart.activeEvent?.event_id || 0,
            attendees: userCart.attendees,
            activePlan: userCart.activePlan || null,
            plan_id: userCart.activePlan?.plan_id || 0,
            atlas_users: userCart.users,
            email: formData.email,
            // company: formData.company,
            address1: formData.address1,
            address2: formData.address2,
            city: formData.city,
            state: formData.state,
            zipcode: formData.zipcode,
            country: formData.country,
            comments: formData.comments,
            purchaseNumber: formData.purchaseNumber,
            contact: formData.contact,
            contactEmail: formData.contactEmail,
            department: formData.department,
        };

        try {
            // Use relative path to avoid CORS/https mismatch in local dev
            const base = window.location.origin || "";
            const endpoint = `/wp-json/dom/v1/checkout/invoice`;

            const res = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            const data = await res.json().catch(() => null);
            if (!res.ok) {
                console.error("Server error:", res.status, data);
                alert("Server error: " + (data?.message || res.statusText || "Unknown"));
            } else {
                // success path depends on your API response
                if (data?.status === "success" || res.status === 200) {
                    //   alert("Invoice submitted successfully!");
                    window.scrollTo({ top: 0, behavior: "instant" });
                    // navigate("/user/thankyou");
                    navigate("/invoice-thankyou");

                    setFormData({
                        email: "",
                        name: "",
                        company: "",
                        address1: "",
                        address2: "",
                        city: "",
                        state: "",
                        zipcode: "",
                        country: "",
                        comments: "",
                        purchaseNumber: "",
                        contact: "",
                        contactEmail: "",
                        department: "",
                    });
                } else {
                    // show server message if present
                    alert("Error: " + (data?.message || "Unknown response"));
                    console.warn("Response body:", data);
                }
            }
        } catch (error) {
            console.error("Fetch error:", error);
            alert("Something went wrong. Try again. See console for details.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        setFormData((prev) => (userCart.paymentMethodInfo ? { ...prev, ...userCart.paymentMethodInfo } : prev));
        if (im_custom.user?.email) {
            setFormData((prev) => ({ ...prev, email: im_custom.user.email }));
        }
    }, []);
    return (
        <div>
            <form onSubmit={handleSubmit} style={{ maxWidth: "54em" }}>
                <div className="row">
                    <div className="col-md-12">
                        <div className="floating-label">
                            <input
                                type="text"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={errors.email ? "error-border" : ""}
                                placeholder=""
                            />

                            <label>Confirm Your Email Address*</label>
                            {errors.email && <small className="error">{errors.email}</small>}
                        </div>
                    </div>
                </div>

                <div className="row">
                    <h3 className="payment-information-heading">Billing Details</h3>
                    <div className="col-md-12">
                        <div className="floating-label">
                            <input
                                type="text"
                                name="purchaseNumber"
                                value={formData.purchaseNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={errors.purchaseNumber ? "error-border" : ""}
                                placeholder=""
                            />
                            <label>Purchase Order Number*</label>
                            <small>If required by your company</small>
                            {errors.purchaseNumber && (
                                <small className="error">{errors.purchaseNumber}</small>
                            )}
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-6">
                        <div className="floating-label">
                            <input
                                type="text"
                                name="contact"
                                value={formData.contact}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={errors.contact ? "error-border" : ""}
                                placeholder=""
                            />
                            <label>Contact (or Entity/Portal Web Address)*</label>
                            {errors.contact && <small className="error">{errors.contact}</small>}
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="floating-label">
                            <input
                                type="text"
                                name="contactEmail"
                                value={formData.contactEmail}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={errors.contactEmail ? "error-border" : ""}
                                placeholder=""
                            />
                            <label>Contact Email*</label>
                            {errors.contactEmail && <small className="error">{errors.contactEmail}</small>}
                        </div>
                    </div>

                    <div className="col-md-12">
                        <div className="floating-label">
                            <input
                                type="text"
                                name="department"
                                value={formData.department}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={errors.department ? "error-border" : ""}
                                placeholder=""
                            />
                            <label>Department*</label>
                            {errors.department && <small className="error">{errors.department}</small>}
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="floating-label">
                            <input
                                type="text"
                                name="address1"
                                value={formData.address1}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={errors.address1 ? "error-border" : ""}
                                placeholder=""
                            />
                            <label>Address 1*</label>
                            {errors.address1 && <small className="error">{errors.address1}</small>}
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="floating-label">
                            <input
                                type="text"
                                name="address2"
                                value={formData.address2}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder=""
                            />
                            <label>Address 2</label>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-4">
                        <div className="floating-label">
                            <input
                                type="text"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={errors.city ? "error-border" : ""}
                                placeholder=""
                            />
                            <label>City*</label>
                            {errors.city && <small className="error">{errors.city}</small>}
                        </div>
                    </div>

                    <div className="col-md-5">
                        <div className="floating-label">
                            <input
                                type="text"
                                name="state"
                                value={formData.state}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={errors.state ? "error-border" : ""}
                                placeholder=""
                            />
                            <label>State/Province*</label>
                            {errors.state && <small className="error">{errors.state}</small>}
                        </div>
                    </div>

                    <div className="col-md-3">
                        <div className="floating-label">
                            {/* zipcode as text so maxLength works */}
                            <input
                                type="text"
                                name="zipcode"
                                maxLength={6}
                                value={formData.zipcode}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={errors.zipcode ? "error-border" : ""}
                                placeholder=""
                            />
                            <label>Postal Code / Zip*</label>
                            {errors.zipcode && <small className="error">{errors.zipcode}</small>}
                        </div>
                    </div>
                </div>

                <div className="floating-label">
                    <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={errors.country ? "error-border" : ""}
                        placeholder=""
                    />
                    <label>Country *</label>
                    {errors.country && <small className="error">{errors.country}</small>}
                </div>

                <div className="floating-label">
                    <textarea
                        name="comments"
                        value={formData.comments}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`invoice-textarea`}
                        placeholder="Special Instructions / Comments"
                    />
                    <label></label>
                    {/* {errors.comments && <small className="error">{errors.comments}</small>} */}
                </div>

                <button type="submit" className="custom-button" disabled={loading}>
                    {loading ? "Submitting..." : "Complete Your Registration"}
                </button>
            </form>

            <style>{`
        .error { color: red; font-size: 12px; margin-top: -2px; display: block; position: absolute;}
        .error-border { border: 1px solid red !important; }
        .floating-label { margin-bottom: 20px; display: block; position: relative; }
        .invoice-textarea {border: 1px solid #CECECE; font-size: 1em; border-radius: 5px; color: #313131; width:100%; padding: 0.5em 1em;}
        .invoice-textarea:focus { outline: none; }
      `}</style>
        </div>
    );
}

export function PrepaidTicket({ im_custom, userCart, onPaymentMethodInfoUpdate }) {

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [errors, setErrors] = useState({});
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        email: im_custom.user?.email || ""
    });

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleBlur = (e) => {
        const { value } = e.target;
        let msg = "";
        if (!value.trim()) msg = "Email is required";
        else if (!emailRegex.test(value)) msg = "Enter a valid email";
        setErrors((prev) => ({ ...prev, email: msg }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage("");

        if (!formData.email.trim() || !emailRegex.test(formData.email)) {
            setErrors({ email: "Enter a valid email" });
            return;
        }

        setLoading(true);

        const payload = {
            email: formData.email || '',
            user_id: im_custom.user.user_id || null,
            type: "prepaid-ticket",
            session_id: userCart.sessionID,
            activeEvent: userCart.activeEvent || null,
            event_id: userCart.activeEvent?.event_id || 0,
            attendees: userCart.attendees,
            activePlan: userCart.activePlan || null,
            plan_id: userCart.activePlan?.plan_id || 0,
            atlas_users: userCart.users,
        };
        try {
            // Use relative path to avoid CORS/https mismatch in local dev
            const base = window.location.origin || "";
            const endpoint = `/wp-json/dom/v1/checkout/invoice`;

            const res = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            const data = await res.json().catch(() => null);
            if (!res.ok) {
                console.error("Server error:", res.status, data);
                alert("Server error: " + (data?.message || res.statusText || "Unknown"));
            } else {
                // success path depends on your API response
                if (data?.status === "success" || res.status === 200) {
                    //   alert("Invoice submitted successfully!");
                    window.scrollTo({ top: 0, behavior: "instant" });
                    // navigate("/user/thankyou");
                    navigate("/prepaid-ticket-thankyou");

                } else {
                    // show server message if present
                    alert("Error: " + (data?.message || "Unknown response"));
                    console.warn("Response body:", data);
                }
            }
        } catch (error) {
            alert("Something went wrong. Try again. See console for details.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (im_custom.user?.email) {
            setFormData((prev) => ({ ...prev, email: im_custom.user.email }));
        }
    }, []);

    return (
        <div>
            <form onSubmit={handleSubmit} style={{ maxWidth: "54em" }}>
                <div className="floating-label">
                    <input
                        type="text"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={errors.email ? "error-border" : ""}
                        placeholder=""
                    />
                    <label>Confirm Your Email Address*</label>
                    {errors.email && <small className="error">{errors.email}</small>}
                </div>

                <button type="submit" className="custom-button" disabled={loading}>
                    {loading ? "Submitting..." : "Complete Your Registration"}
                </button>

            </form>

            <style>{`
                .floating-label { margin-bottom: 20px; display: block; position: relative; }
                .error { color: red; font-size: 12px; margin-top: -2px; display: block; position: absolute; }
                .error-border { border: 1px solid red !important; }
            `}</style>
        </div>
    );
}

/* Create DB session */
export const createDBSession = (sessionID, user_id, cart) => {
    // Simulate DB session creation
    console.log("Creating DB session:", sessionID, user_id, cart);
    fetch(config.apiBaseUrl + 'cart/session/create', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ session_id: sessionID, user_id: user_id, cart: cart }),
    })
        .then(response => response.json())
        .then(data => {
            console.log("DB session created:", data);
        })
        .catch(error => {
            console.error("Error creating DB session:", error);
        });
};

export const updateDBSession = (sessionID, user_id, cart) => {
    // Simulate DB session update
    console.log("Updating DB session:", sessionID, user_id, cart);
    fetch(config.apiBaseUrl + 'cart/session/update', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ session_id: sessionID, user_id: user_id, cart: cart }),
    })
        .then(response => response.json())
        .then(data => {
            console.log("DB session updated:", data);
        })
        .catch(error => {
            console.error("Error updateing DB session:", error);
        });
};

export const deleteDBSession = (sessionID) => {
    // Simulate DB session update
    console.log("Updating DB session:", sessionID);
    fetch(config.apiBaseUrl + 'cart/session/delete', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ session_id: sessionID }),
    })
        .then(response => response.json())
        .then(data => {
            console.log("DB session deleted:", data);
        })
        .catch(error => {
            console.error("Error deleting DB session:", error);
        });
};
export const clearUserCartLocalStorage = () => {
    localStorage.removeItem('user_cart');
}

export function PaymentPrintReceipt({ im_custom, userCart, orderId, grandTotal, estimatedTax = 0, paymentDate, paymentMethodDetails, receiptHeading = "", receiptParagraph = "" }) {
    const totalUsers = userCart.users?.length || 0;
    const defaultUsers = userCart.users?.filter((u) => u.isDefault === "1").length || 0;
    const additionalUsers = userCart.users?.filter((u) => u.isDefault === "0").length || 0;

    const assignedUsers =
        userCart.users?.filter(
            (u) =>
                (u.firstName && u.firstName.trim() !== "") &&
                (u.lastName && u.lastName.trim() !== "") &&
                (u.email && u.email.trim() !== "")
        ).length || 0;

    const unassignedUsers = totalUsers - assignedUsers;

    const totalAttendees = userCart.attendees?.length || 0;
    const assignedAttendees =
        userCart.attendees?.filter((a) => a.leaveSeatUnassigned != "1").length || 0;
    const remainingAttendees = totalAttendees - assignedAttendees;

    return (
        <div id="thankyou-print-section" style={{ maxWidth: "1024px" }}>
            <table width="100%" cellPadding="0" cellSpacing="0" style={{ borderBottom: "1px solid #ddd", marginBottom: "20px", paddingTop: "30px" }}>
                <tbody>
                    <tr>
                        <td>
                            <h1 style={{ fontSize: "26px", margin: 0 }}>{receiptHeading}</h1>
                            <p style={{ margin: "6px 0 0", fontSize: "14px", color: "#555", paddingBottom: "15px", maxWidth: "500px" }}>{receiptParagraph}</p>
                            {orderId ? (
                                <p className="public-desc">Order ID#: {orderId}</p>
                            ) : null}
                            {paymentDate ? (
                                <p className="public-desc"><PaymentDateFormat input={paymentDate} /></p>
                            ) : null}
                        </td>
                        <td width="60" style={{ paddingBottom: "15px" }}>
                            <img src="/wp-content/themes/dom/assets/img/directions-logo-simple.png" width="100" style={{ width: "40px" }} />
                        </td>
                    </tr>
                </tbody>
            </table>
            {userCart.activePlan?.plan_label && (
                <h2 style={{ fontSize: "18px", marginBottom: "10px" }}>
                    {userCart.activePlan?.plan_label && `Plan : ${userCart.activePlan.plan_label}`}
                </h2>
            )}
            {userCart.activePlan && userCart.activePlan.plan_id && (
                <table width="100%" cellPadding="0" cellSpacing="0" className="item-table">
                    <tbody>
                        <tr>
                            <td width="120">
                                <img src="/wp-content/themes/dom/assets/img/atlas-laptop.webp" width="100" style={{ borderRadius: "6px" }} />
                            </td>
                            <td>
                                <h3 style={{ margin: 0, fontSize: "16px" }}>Atlas Membership</h3>

                                <p style={{ margin: "4px 0 0", fontSize: "14px" }}>
                                    {assignedUsers} of {totalUsers} Users Assigned <br />

                                    {unassignedUsers > 0 ? (
                                        <>
                                            You have {unassignedUsers} unassigned users. <br />
                                        </>
                                    ) : (
                                        "All users are assigned"
                                    )}
                                </p>
                                <h4 style={{ margin: "10px 0 4px", fontSize: "14px" }}>Assigned Users:</h4>
                                <div style={{ fontSize: "13px", color: "#555" }}>
                                    {userCart.users
                                        .filter((a) => a.leaveSeatUnassigned === "0")
                                        .map((a, i) => (
                                            <p key={i} style={{ margin: "4px 0 0", fontSize: "12px" }}>
                                                {a.firstName} {a.lastName} ({a.email})
                                            </p>
                                        ))}

                                    {assignedUsers === 0 && <p>No assigned users.</p>}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            )}
            {userCart?.attendees?.length > 0 && (
                <h3>
                    {userCart.activeEvent?.title ? `Event : ${userCart.activeEvent.title}` : 'Event : Not selected'}
                </h3>
            )}
            {totalAttendees > 0 && (
                <table width="100%" cellPadding="0" cellSpacing="0" className="item-table">
                    <tbody>
                        <tr>
                            <td width="120">
                                <img src="/wp-content/themes/dom/assets/img/LBC-ticket-thumbnail.png" width="100" style={{ borderRadius: "6px" }} />
                            </td>

                            <td>
                                <h3 style={{ margin: 0, fontSize: "16px" }}>Microsoft Licensing Training</h3>
                                <p style={{ margin: "4px 0 0", fontSize: "14px" }}>
                                    {assignedAttendees} of {totalAttendees} Attendees Assigned <br />

                                    {remainingAttendees > 0 ? (
                                        <>
                                            You can still assign {remainingAttendees} attendee

                                        </>
                                    ) : (
                                        "All attendees are assigned"
                                    )}
                                </p>
                                <h4 style={{ margin: "10px 0 4px", fontSize: "14px" }}>Assigned Attendees:</h4>
                                <div style={{ fontSize: "13px", color: "#555" }}>
                                    {userCart.attendees
                                        .filter((a) => a.leaveSeatUnassigned === "0")
                                        .map((a, i) => (
                                            <p key={i} style={{ margin: "4px 0 0", fontSize: "12px" }}>
                                                {a.firstName} {a.lastName} ({a.email})
                                            </p>
                                        ))}

                                    {assignedAttendees === 0 && <p>No assigned attendees.</p>}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            )}
            <table width="100%" cellPadding="0" cellSpacing="0" style={{ marginTop: "25px" }}>
                <tbody>
                    <tr>
                        <td>
                            <h3 style={{ fontSize: "18px", margin: "0 0 8px" }}>Purchased by</h3>
                            <p style={{ margin: 0, fontSize: "14px" }}>{im_custom.user.name}</p>
                            <p style={{ margin: 0, fontSize: "14px" }}>{im_custom.user.email}</p>
                            <p style={{ margin: "0 0 10px", fontSize: "14px" }}>{im_custom.user.phone}</p>
                        </td>

                        <td align="right">
                            <p style={{ margin: 0, fontSize: "14px" }}>{im_custom.user.company}</p>
                            <p style={{ margin: 0, fontSize: "14px" }}>{im_custom.user.country}</p>
                        </td>
                    </tr>
                </tbody>
            </table>
            {paymentMethodDetails && (<table width="100%" cellPadding="0" cellSpacing="0" style={{ marginTop: "25px" }}>
                <tbody>
                    <tr>
                        <td>
                            <h3 style={{ fontSize: "18px", margin: "0 0 8px" }}>Payment Details</h3>
                            {paymentMethodDetails?.card?.display_brand && (<p style={{ margin: 0, fontSize: "14px" }}>
                                Brand: {paymentMethodDetails?.card?.display_brand}
                            </p>)}
                            {paymentMethodDetails?.card?.last4 && (<p style={{ margin: 0, fontSize: "14px" }}>
                                Card Number: XXXX-XXXX-XXXX-{paymentMethodDetails?.card?.last4}
                            </p>)}
                            {paymentMethodDetails?.card?.exp_month && paymentMethodDetails?.card?.exp_year && (<p style={{ margin: "0 0 10px", fontSize: "14px" }}>
                                Expiration: {paymentMethodDetails?.card?.exp_month}/{paymentMethodDetails?.card?.exp_year}
                            </p>)}
                        </td>
                    </tr>
                </tbody>
            </table>)}
            <table width="100%" cellPadding="0" cellSpacing="0" style={{ marginTop: "25px", borderTop: "1px solid #ddd", paddingTop: "15px" }}>
                <tbody>
                    <tr>
                        <td>
                            <h3 style={{ fontSize: "18px", margin: "0 0 8px" }}>Order Summary</h3>
                        </td>
                    </tr>
                    {userCart.activePlan && (
                        <tr>
                            <td style={{ fontSize: "14px", borderTop: "1px solid #eee", fontWeight: "bold", paddingTop: "10px", paddingBottom: "10px" }}>
                                Atlas Membership
                            </td>
                            <td align="right" style={{ fontSize: "14px", fontWeight: "bold", borderTop: "1px solid #eee", paddingTop: "10px", paddingBottom: "10px" }}>
                                {formatPrice(userCart.activePlan.price)}
                            </td>
                        </tr>
                    )}
                    {userCart.activePlan && userCart.users.filter((item) => item.isDefault === "0").length > 0 && (<tr>
                        <td style={{ fontSize: "14px", borderTop: "1px solid #eee", paddingTop: "10px", paddingBottom: "10px" }}>
                            Additional Users({userCart.users.filter((item) => item.isDefault === "0").length})
                        </td>
                        <td align="right" style={{ fontSize: "14px", borderTop: "1px solid #eee", paddingTop: "10px", paddingBottom: "10px" }}>
                            {formatPrice(userCart.users
                                .filter((item) => item.isDefault === "0")
                                .reduce((total, item) => total + item.price, 0))}
                        </td>
                    </tr>)}

                    {userCart.activePlan && (<tr>
                        <td style={{ fontSize: "14px", borderTop: "1px solid #eee", fontWeight: "bold", paddingTop: "10px", paddingBottom: "10px" }}>
                            Subtotal
                        </td>
                        <td align="right" style={{ fontSize: "14px", fontWeight: "bold", borderTop: "1px solid #eee", paddingTop: "10px", paddingBottom: "10px" }}>
                            {formatPrice(userCart.activePlan.price + userCart.users
                                .filter((item) => item.isDefault === "0")
                                .reduce((total, item) => total + item.price, 0))} <br /> <span style={{ fontSize: "12px", fontWeight: "400", }} >12 Month Subscription</span>
                        </td>
                    </tr>)}
                    {userCart.attendees.length > 0 && (
                        <tr>
                            <td style={{ fontSize: "14px", borderTop: "1px solid #eee", paddingTop: "10px", paddingBottom: "10px" }}>
                                Microsoft Licensing Training
                            </td>
                            <td align="right" style={{ fontSize: "14px", borderTop: "1px solid #eee", paddingTop: "10px", paddingBottom: "10px" }}>
                                {userCart.activePlan?.training_tickets > 0 ? (
                                    <>
                                        <span style={{ textDecoration: "line-through", marginRight: "8px", opacity: 0.6, }}>
                                            {userCart.activePlan.training_tickets == 1 && (formatPrice(im_custom.event_price.price))}
                                            {userCart.activePlan.training_tickets > 1 && (formatPrice(im_custom.event_price.price + (userCart.activePlan.training_tickets - 1) * im_custom.event_price.extra_training_tickets_price))}
                                        </span>
                                        <span style={{ display: "block", fontSize: "14px", }}>
                                            {(() => {
                                                const free = userCart.activePlan.training_tickets;
                                                const selected = userCart.activeEvent?.training_tickets || 0;
                                                const extra = Math.max(selected - free, 0);
                                                return formatPrice(extra * userCart.activePlan.extra_training_tickets_price);
                                            })()} (Included in plan)
                                        </span>
                                    </>
                                ) : (
                                    formatPrice(getFirstTicketPrice(userCart, im_custom))
                                )}
                            </td>
                        </tr>
                    )}
                    {userCart.attendees.filter((i) => i.isDefault === "0").length > 0 && (
                        <tr>
                            <td style={{ fontSize: "14px", borderTop: "1px solid #eee", paddingTop: "10px", paddingBottom: "10px" }}>
                                Additional Attendees ({userCart.attendees.filter((i) => i.isDefault === "0").length})
                            </td>
                            <td align="right" style={{ fontSize: "14px", borderTop: "1px solid #eee", paddingTop: "10px", paddingBottom: "10px" }}>
                                {formatPrice(userCart.attendees.filter((i) => i.isDefault === "0").reduce((t, i) => t + i.price, 0))}
                            </td>
                        </tr>
                    )}

                    {(() => {
                        if (userCart.attendees.length == 0) return null;
                        const subtotal = calculateTrainingSubtotal(userCart, im_custom);
                        return (
                            <tr>
                                <td style={{ fontSize: "14px", borderTop: "1px solid #eee", paddingTop: "10px", paddingBottom: "10px" }}>
                                    <strong>Subtotal</strong>
                                </td>
                                <td align="right" style={{ fontSize: "14px", fontWeight: "bold", borderTop: "1px solid #eee", paddingTop: "10px", paddingBottom: "10px" }}>
                                    {formatPrice(subtotal)}
                                </td>
                            </tr>
                        );
                    })()}
                    {estimatedTax > 0 && (<tr>
                        <td style={{ fontWeight: "bold", borderTop: "1px solid #ddd", paddingTop: "10px", paddingBottom: "10px" }}>
                            <strong>Tax</strong>
                        </td>
                        <td align="right" style={{ fontSize: "16px", borderTop: "1px solid #ddd", paddingTop: "10px", paddingBottom: "10px" }}>
                            {formatPrice(estimatedTax)}
                        </td>
                    </tr>)}
                    <tr>
                        <td style={{ fontWeight: "bold", borderTop: "1px solid #ddd", paddingTop: "10px", paddingBottom: "10px" }}>
                            Grand Total
                        </td>
                        <td align="right" style={{ fontSize: "16px", fontWeight: "bold", borderTop: "1px solid #ddd", paddingTop: "10px", paddingBottom: "10px" }}>
                            {formatPrice(grandTotal + estimatedTax)}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}